import * as React from 'react';
import { Box, Chip, Stack } from '@mui/material';
import { Cancel, CheckCircle, Schedule } from '@mui/icons-material';
import { Identifier } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { ContractSigningStatus, ProjectContractSigning } from '@/types';
import { formatFlowDate, phaseStatusColor, stepStatusLabel } from '../flow/projectFlowUtils';
import { fetchProjectContractSigning } from './contractSigningApi';

interface SignatureChipProps {
    label: string;
    status: ContractSigningStatus;
    signedAt?: string | null;
    signer?: string | null;
}

const SignatureChip: React.FC<SignatureChipProps> = ({ label, status, signedAt, signer }) => {
    const color = status === 'declined' ? 'error' : phaseStatusColor(status);
    const icon =
        status === 'done' ? (
            <CheckCircle sx={{ fontSize: 16 }} />
        ) : status === 'declined' ? (
            <Cancel sx={{ fontSize: 16 }} />
        ) : status === 'in_progress' ? (
            <Schedule sx={{ fontSize: 16 }} />
        ) : undefined;
    const text =
        status === 'done'
            ? [label, signer, formatFlowDate(signedAt)].filter(Boolean).join(' · ')
            : status === 'declined'
              ? `${label} · Declined`
              : `${label} · ${stepStatusLabel(status)}`;
    return (
        <Chip
            size="small"
            color={color}
            variant={color === 'default' ? 'outlined' : 'filled'}
            icon={icon}
            label={text}
        />
    );
};

/**
 * The MRT and client signature status chips, shown under the "Contract signed" step.
 * Reuses the same ['contract-signing', projectId] query as the signing panel, so the
 * React Query cache is shared (no extra request).
 */
export const ContractSignatureChips: React.FC<{ projectId: Identifier }> = ({ projectId }) => {
    const { data } = useQuery<ProjectContractSigning>({
        queryKey: ['contract-signing', projectId],
        queryFn: () => fetchProjectContractSigning(projectId),
    });
    const signing = data?.signing ?? null;
    if (!signing) return null;

    return (
        <Box sx={{ pl: 1, pb: 1 }}>
            <Stack direction="row" gap={1} flexWrap="wrap" aria-label="Signature status">
                <SignatureChip
                    label="MRT signature"
                    status={signing.mrt_status}
                    signedAt={signing.mrt_signed_at}
                    signer={signing.mrt_signer?.name || signing.mrt_signer?.email}
                />
                <SignatureChip
                    label="Client signature"
                    status={signing.client_status}
                    signedAt={signing.client_signed_at}
                    signer={signing.client_name || signing.client_email}
                />
            </Stack>
        </Box>
    );
};

ContractSignatureChips.displayName = 'ContractSignatureChips';
